// Consultancy-firm detail + certifications (DESIGN-VS-BUILD-GAPS-R2 §B). The firm CRUD list could create/edit
// firm basics but had no drill-in to manage a firm's professional certifications (consultancy_firm_certification:
// ISO 27001, CIPP/E, …). This screen shows the firm meta + a certifications list with add/remove. Wired through
// @sympllex/api-client against the admin-worker (global, RLS-free, super_tenant_admin-gated server-side).
import { useState } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ShieldCheck, Trash2 } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CERT_KINDS: [string, string][] = [
  ["iso27001_lead_auditor", "ISO 27001 Lead Auditor"],
  ["iso27001_lead_implementer", "ISO 27001 Lead Implementer"],
  ["cipp_e", "CIPP/E"],
  ["cipm", "CIPM"],
  ["cipt", "CIPT"],
  ["cdpo_nl", "CDPO (NL)"],
  ["fip", "FIP"],
  ["other", "Other"],
];
const certLabel = (k: string) => CERT_KINDS.find(([v]) => v === k)?.[1] ?? k;

interface Cert { id: string; certification: string; certificationOtherText: string | null; issuerName: string | null; issuedAt: string | null; expiresAt: string | null; certificateUrl: string | null }
interface FirmDetail {
  id: string; legalName: string; tradeName: string | null; registrationNumber: string | null;
  primaryJurisdictionId: string; is9SprintsCertified: boolean | null; defaultSprintCadenceWeeks: number | null;
  isActive: boolean; certifications: Cert[];
}
const fmt = (d: string | null) => (d ? d.slice(0, 10) : "—");
const errMsg = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error)?.message ?? "error");

export function ConsultancyFirmDetailRoute() {
  const { firmId } = useParams({ from: "/consultancy-firms/$firmId" });
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["firm-detail", firmId] });

  const { data, isLoading, error } = useQuery({ queryKey: ["firm-detail", firmId], queryFn: () => api.getConsultancyFirmDetail(firmId) as unknown as Promise<FirmDetail> });
  const d = data;

  const [kind, setKind] = useState("iso27001_lead_auditor");
  const [otherText, setOtherText] = useState("");
  const [issuer, setIssuer] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [url, setUrl] = useState("");

  const add = useMutation({
    mutationFn: () => api.addFirmCertification(firmId, {
      certification: kind,
      ...(kind === "other" && otherText.trim() ? { certificationOtherText: otherText.trim() } : {}),
      ...(issuer.trim() ? { issuerName: issuer.trim() } : {}),
      ...(issuedAt ? { issuedAt } : {}),
      ...(expiresAt ? { expiresAt } : {}),
      ...(url.trim() ? { certificateUrl: url.trim() } : {}),
    }),
    onSuccess: () => { setOtherText(""); setIssuer(""); setIssuedAt(""); setExpiresAt(""); setUrl(""); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (certificationId: string) => api.deleteFirmCertification(firmId, certificationId), onSuccess: invalidate });

  return (
    <section>
      <Link to="/consultancy-firms" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Back to firms
      </Link>

      {isLoading ? (
        <div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-40 w-full" /></div>
      ) : error ? (
        <p className="text-sm text-destructive">Failed: {errMsg(error)}</p>
      ) : !d ? (
        <p className="text-sm text-muted-foreground">Firm not found.</p>
      ) : (
        <>
          <PageHeader title={d.legalName} {...(d.tradeName ? { description: d.tradeName } : {})} />
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {d.is9SprintsCertified && <Badge variant="secondary">9-sprints certified</Badge>}
            {d.defaultSprintCadenceWeeks != null && <Badge variant="outline">cadence {d.defaultSprintCadenceWeeks}wk</Badge>}
            <Badge variant={d.isActive !== false ? "secondary" : "outline"}>{d.isActive !== false ? "active" : "inactive"}</Badge>
            {d.registrationNumber && <Badge variant="outline">reg. {d.registrationNumber}</Badge>}
          </div>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="size-4" />Certifications</CardTitle></CardHeader>
            <CardContent>
              {d.certifications.length === 0 ? (
                <p className="text-sm text-muted-foreground">No certifications recorded for this firm.</p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {d.certifications.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 border-b py-2.5 text-sm last:border-b-0">
                      <span className="min-w-0">
                        <span className="font-medium">{c.certification === "other" ? (c.certificationOtherText ?? "Other") : certLabel(c.certification)}</span>
                        {c.issuerName && <span className="text-muted-foreground"> · {c.issuerName}</span>}
                        <span className="block text-xs text-muted-foreground">
                          {c.issuedAt ? `issued ${fmt(c.issuedAt)}` : "issued —"}{c.expiresAt ? ` · expires ${fmt(c.expiresAt)}` : ""}
                          {c.certificateUrl ? <> · <a href={c.certificateUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">certificate</a></> : null}
                        </span>
                      </span>
                      <Can perm="super_tenant.canonical_library.write" fallback={null}>
                        <Button size="xs" variant="outline" disabled={remove.isPending} onClick={() => remove.mutate(c.id)} aria-label="Remove certification"><Trash2 className="size-3.5" /></Button>
                      </Can>
                    </li>
                  ))}
                </ul>
              )}

              <Can perm="super_tenant.canonical_library.write">
                <form
                  className="mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2"
                  onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label>Certification</Label>
                    <Select value={kind} onValueChange={setKind}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CERT_KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {kind === "other" && (
                    <div className="flex flex-col gap-1.5">
                      <Label>Other (name)</Label>
                      <Input value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Certification name" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <Label>Issuer</Label>
                    <Input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="e.g. IAPP, BSI" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Issued</Label>
                    <Input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Expires</Label>
                    <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Certificate URL</Label>
                    <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
                  </div>
                  <div className="flex items-end gap-2 sm:col-span-2">
                    <Button type="submit" disabled={add.isPending}>Add certification</Button>
                    {(add.isError || remove.isError) && <span className="text-sm text-destructive">{errMsg(add.error ?? remove.error)}</span>}
                  </div>
                </form>
              </Can>
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
}
